import { useCallback, useMemo, useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../../redux/store";
import { apiPost } from "../../../../../services/api/apiClient";
import {
  TextField,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  // Avatar,
  // Input,
  FormControl,
  InputLabel,
  Chip,
  Divider,
  Alert,
  Autocomplete,
  Switch,
  FormControlLabel,
  Avatar,
  CircularProgress,
  } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useSnackbar } from "notistack";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";

import {
  createUser,
  updateUser,
  closeDrawer,
  handleUserChange,
  handleProjectChange,
  handlePermissionsChange,
} from "../../usersSlice";
import { AVAILABLE_PERMISSIONS, DEFAULT_PERMISSIONS } from "../../constants";
import {
  updateProfileImage,
  deleteProfileImage,
} from "../../../../../redux/slices/profileSlice";

const roleOptions = ["admin", "client", "employee"];

const Info = () => {
  const {
    selectedUser,
    changes,
    userId,
    updateUserStatus,
    createUserStatus,
    fetchUserStatus,
    deleteUserStatus,
    fetchUserError,
    createUserError,
    updateUserError,
    deleteUserError,
  } = useAppSelector((state) => state.users) as any;
  const { user } = useAppSelector((state) => state.login) as any;
  const { imageUploading, imageDeleting, imageError, imageDeleteError } =
    useAppSelector((state) => state.profile) as any;
  const { enqueueSnackbar } = useSnackbar();

  const [resendingEmail, setResendingEmail] = useState(false);
  const [activatingUser, setActivatingUser] = useState(false);

  const customers = useAppSelector((state) => state.customers?.customers) as any;

  const projectsList = useMemo(() => {
    if (!customers) {
      return [];
    }
    // A project without a name is a row from the other book or a deleted
    // customer: it can be assigned to a user but cannot be labelled, so it
    // shows as an empty chip. Leave those out of the list entirely.
    const seen = new Set<string>();
    return (customers as any[])
      .filter(
        (customer) =>
          customer?.id != null &&
          typeof customer.name === "string" &&
          customer.name.trim() !== "",
      )
      .filter((customer) => {
        const id = String(customer.id);
        if (seen.has(id)) {
          return false;
        }
        seen.add(id);
        return true;
      })
      .map((customer) => ({
        value: String(customer.id),
        label: customer.name.trim(),
      }));
  }, [customers]);

  // Resolve the assigned ids to options we can label. An assignment from the
  // other book has no option here, and leaving the undefined in the value is
  // what produced the blank chips — and made the change handler throw before
  // anything was saved.
  const selectedProjects = useMemo(() => {
    const ids = changes?.projects || selectedUser?.projects || [];
    return (ids as any[])
      .map((id) =>
        projectsList.find((project) => project.value === String(id)),
      )
      .filter(Boolean);
  }, [changes?.projects, selectedUser?.projects, projectsList]);

  const dispatch = useAppDispatch();

  const onClose = () => {
    dispatch(closeDrawer());
  };

  const handleChange = useCallback(
    (e: any) => {
      const { name, value } = e.target;
      dispatch(handleUserChange({ name, value }));
    },
    [dispatch],
  );

  const handleSelect = useCallback(
    (name: any) => (e: any) => {
      dispatch(handleUserChange({ name, value: e.target.value }));
    },
    [dispatch],
  );

  // const onProjectChange = useCallback(
  //   (e: any, selected) => {
  //     dispatch(handleProjectChange(e.target.value));
  //   },
  //   [dispatch]
  // );

  const handlePermissionChange = useCallback(
    (permission: any) => (event: any) => {
      dispatch(
        handlePermissionsChange({ permission, value: event.target.checked }),
      );
    },
    [dispatch],
  );

  // Image upload handlers
  const handleImageUpload = useCallback(
    (event: any) => {
      const file = event.target.files[0];
      if (file && userId) {
        // Validate file
        if (!file.type.startsWith("image/")) {
          enqueueSnackbar("Please select an image file", { variant: "error" });
          return;
        }
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
          enqueueSnackbar("Image must be smaller than 10MB", {
            variant: "error",
          });
          return;
        }

        dispatch(updateProfileImage({ imageFile: file, userId }));
      }
      // Clear input
      event.target.value = "";
    },
    [dispatch, userId, enqueueSnackbar],
  );

  const handleImageDelete = useCallback(() => {
    if (userId) {
      dispatch(deleteProfileImage(userId));
    }
  }, [dispatch, userId]);

  const handleResendActivationEmail = useCallback(async () => {
    const email = selectedUser?.email;
    if (!email) {
      enqueueSnackbar("User email is required", { variant: "error" });
      return;
    }

    setResendingEmail(true);
    try {
      await apiPost({
        endpoint: "/resend-activation-email",
        data: { email },
      });
      enqueueSnackbar("Activation email sent successfully", {
        variant: "success",
      });
    } catch (error: any) {
      enqueueSnackbar(error.message || "Failed to send activation email", {
        variant: "error",
      });
    } finally {
      setResendingEmail(false);
    }
  }, [selectedUser?.email, enqueueSnackbar]);

  const handleActivateUser = useCallback(async () => {
    if (!userId) {
      enqueueSnackbar("User ID is required", { variant: "error" });
      return;
    }

    setActivatingUser(true);
    try {
      await dispatch(updateUser({ id: userId, activate: true })).unwrap();
      enqueueSnackbar("User activated successfully", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(error.message || "Failed to activate user", {
        variant: "error",
      });
    } finally {
      setActivatingUser(false);
    }
  }, [userId, dispatch, enqueueSnackbar]);

  const handleSubmit = useCallback(
    (e: any) => {
      e.preventDefault();

      if (!changes) {
        enqueueSnackbar("No changes to save", { variant: "warning" });
        return;
      }

      const { firstName, lastName, middleName, ...user } = changes;

      user.name = user.name || {};

      // Merge name fields; use empty string explicitly so the server can validate
      user.name.first = firstName ?? selectedUser?.firstName ?? "";
      user.name.middle = middleName ?? selectedUser?.middleName ?? "";
      user.name.last = lastName ?? selectedUser?.lastName ?? "";

      // Client-side validation for required fields
      if (!userId) {
        if (!user.name.first.trim()) {
          enqueueSnackbar("First name is required", { variant: "error" });
          return;
        }
        if (!user.name.last.trim()) {
          enqueueSnackbar("Last name is required", { variant: "error" });
          return;
        }
        const email = user.email ?? selectedUser?.email ?? "";
        if (!email.trim()) {
          enqueueSnackbar("Email is required", { variant: "error" });
          return;
        }
        const role = user.role ?? selectedUser?.role ?? "";
        if (!role) {
          enqueueSnackbar("Role is required", { variant: "error" });
          return;
        }
      }

      if (userId) {
        dispatch(
          updateUser({
            id: userId,
            ...user,
          }),
        );
      } else {
        dispatch(
          createUser({
            ...user,
          }),
        );
      }
    },

    [dispatch, changes, userId, selectedUser, enqueueSnackbar],
  );

  // const handleImageUpload = (e: any) => {
  //   const file = e.target.files[0];
  //   if (file) {
  //     const reader = new FileReader();
  //     reader.onloadend = () => {
  //       setFormData((prev) => ({ ...prev, profileImage: reader.result }));
  //     };
  //     reader.readAsDataURL(file);
  //   }
  // };

  const isLoading = useMemo(() => {
    return (
      updateUserStatus === "loading" ||
      createUserStatus === "loading" ||
      fetchUserStatus === "loading" ||
      deleteUserStatus === "loading" ||
      imageUploading ||
      imageDeleting ||
      resendingEmail ||
      activatingUser
    );
  }, [
    updateUserStatus,
    createUserStatus,
    fetchUserStatus,
    deleteUserStatus,
    imageUploading,
    imageDeleting,
    resendingEmail,
    activatingUser,
  ]);

  return (
    <>
      {/* {fetchUserStatus === "succeeded" && (
          <Alert severity="success" sx={{ mb: 2 }}>
            User fetched successfully
          </Alert>
        )} */}
      {createUserStatus === "succeeded" && (
        <Alert severity="success" sx={{ mb: 2 }}>
          User created successfully
        </Alert>
      )}
      {updateUserStatus === "succeeded" && (
        <Alert severity="success" sx={{ mb: 2 }}>
          User updated successfully
        </Alert>
      )}
      {deleteUserStatus === "succeeded" && (
        <Alert severity="success" sx={{ mb: 2 }}>
          User deleted successfully
        </Alert>
      )}

      {fetchUserError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {fetchUserError}
        </Alert>
      )}
      {createUserError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {createUserError}
        </Alert>
      )}
      {updateUserError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {updateUserError}
        </Alert>
      )}
      {deleteUserError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {deleteUserError}
        </Alert>
      )}

      {/* Image upload notifications */}
      {imageError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to upload image: {imageError}
        </Alert>
      )}
      {imageDeleteError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to delete image: {imageDeleteError}
        </Alert>
      )}

      {/* Profile Image Section */}
      <Divider sx={{ mb: 2 }} textAlign="left">
        Profile Image
      </Divider>

      <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3 }}>
        <Avatar src={selectedUser?.photo} sx={{ width: 80, height: 80 }} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              component="label"
              size="small"
              disabled={imageUploading || imageDeleting}
              startIcon={
                imageUploading ? <CircularProgress size={24} /> : <UploadIcon />
              }
            >
              {imageUploading ? "Uploading..." : "Upload Image"}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageUpload}
              />
            </Button>
            {selectedUser?.photo && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleImageDelete}
                disabled={imageUploading || imageDeleting}
                startIcon={
                  imageDeleting ? (
                    <CircularProgress size={24} />
                  ) : (
                    <DeleteIcon />
                  )
                }
              >
                {imageDeleting ? "Deleting..." : "Delete"}
              </Button>
            )}
          </Box>
          <Typography variant="caption" color="textSecondary">
            Supports JPG, PNG, GIF up to 10MB
          </Typography>
        </Box>
      </Box>

      {/* Account Status Section */}
      {selectedUser?.profileStatus === "pendingactivation" && (
        <>
          <Divider sx={{ mb: 2 }} textAlign="left">
            Account Status
          </Divider>
          <Box
            sx={{
              p: 2,
              mb: 3,
              border: "1px solid",
              borderColor: "warning.main",
              borderRadius: 0,
              backgroundColor: "warning.lighter",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="body2"
                fontWeight="bold"
                color="warning.dark"
              >
                Pending Activation
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This user has not activated their account yet
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={handleResendActivationEmail}
              disabled={isLoading || !selectedUser?.email}
              startIcon={
                resendingEmail ? <CircularProgress size={24} /> : <EmailIcon />
              }
            >
              {resendingEmail ? "Sending..." : "Resend Email"}
            </Button>
          </Box>
        </>
      )}

      {selectedUser?.profileStatus === "inactive" && (
        <>
          <Divider sx={{ mb: 2 }} textAlign="left">
            Account Status
          </Divider>
          <Box
            sx={{
              p: 2,
              mb: 3,
              border: "1px solid",
              borderColor: "error.main",
              borderRadius: 0,
              backgroundColor: "error.lighter",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight="bold" color="error.dark">
                Inactive Account
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This user account is currently inactive
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={handleActivateUser}
              disabled={isLoading}
              startIcon={
                activatingUser ? (
                  <CircularProgress size={24} />
                ) : (
                  <CheckCircleIcon />
                )
              }
            >
              {activatingUser ? "Activating..." : "Activate"}
            </Button>
          </Box>
        </>
      )}

      {!userId && (
        <>
          <Divider sx={{ mt: 3, mb: 2 }} textAlign="left">
            Activation Settings
          </Divider>
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={changes?.activate || false}
                  onChange={(event) => {
                    dispatch(
                      handleUserChange({
                        name: "activate",
                        value: event.target.checked,
                      }),
                    );
                  }}
                  disabled={isLoading}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Activate Immediately</Typography>
                  <Typography variant="caption" color="text.secondary">
                    If enabled, user will be activated and receive an activation
                    email
                  </Typography>
                </Box>
              }
            />
          </Box>
        </>
      )}

      <Divider sx={{ mb: 2 }} textAlign="left">
        Personal Information
      </Divider>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            type="text"
            fullWidth
            size="small"
            label="First Name"
            name="firstName"
            value={
              changes?.firstName === undefined
                ? selectedUser?.firstName || ""
                : changes?.firstName
            }
            onChange={handleChange}
            disabled={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            type="text"
            fullWidth
            size="small"
            label="Middle Name"
            name="middleName"
            value={
              changes?.middleName === undefined
                ? selectedUser?.middleName || ""
                : changes?.middleName
            }
            onChange={handleChange}
            disabled={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            type="text"
            fullWidth
            size="small"
            label="Last Name"
            name="lastName"
            value={
              changes?.lastName === undefined
                ? selectedUser?.lastName || ""
                : changes?.lastName
            }
            onChange={handleChange}
            disabled={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Role</InputLabel>
            <Select
              disabled={isLoading}
              value={changes?.role || selectedUser?.role || ""}
              label="Role"
              onChange={handleSelect("role")}
            >
              {roleOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {["admin", "super-admin"].includes(selectedUser?.role) && (
          <>
          <Grid size={{ xs: 12, md: 6 }}>
            {/* <FormControl fullWidth size="small"> */}
            {/* <InputLabel>Position</InputLabel> */}
            <TextField
              type="text"
              fullWidth
              size="small"
              label="Job Title"
              name="position"
              value={
                changes?.position === undefined
                  ? selectedUser?.position || ""
                  : changes?.position
              }
              onChange={handleChange}
              disabled={isLoading}
            />
            {/* </FormControl> */}
          </Grid>

        <Grid size={{ xs: 12, md: 12 }}>
            {/* <FormControl fullWidth size="small"> */}
            {/* <InputLabel>Position</InputLabel> */}
            <TextField

              fullWidth
              multiline
              size="small"
              label="Role description "
              name="roleDescription"
              value={
                changes?.roleDescription  === undefined
                  ? selectedUser?.roleDescription || ""
                  : changes?.roleDescription
              }
              // onChange={handleChange}
              onChange={handleChange}
              disabled={isLoading}
               sx={{ mb: 1 }}
            />
            {/* </FormControl> */}  
          </Grid>
          </>
)}
      </Grid>
      
                


      <Divider sx={{ mt: 3, mb: 2 }} textAlign="left">
        Contact Information
      </Divider>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            type="email"
            fullWidth
            size="small"
            label="Email"
            name="email"
            value={
              changes?.email === undefined
                ? selectedUser?.email || ""
                : changes?.email
            }
            onChange={handleChange}
            disabled={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            type="tel"
            fullWidth
            size="small"
            label="Phone"
            name="phone"
            value={
              changes?.phone === undefined
                ? selectedUser?.phone || ""
                : changes?.phone
            }
            onChange={handleChange}
            disabled={isLoading}
          />
        </Grid>
      </Grid>

      {user?.role === "super-admin" && (
        <>
          <Divider sx={{ mt: 3, mb: 2 }} textAlign="left">
            Permissions
          </Divider>
          <Box sx={{ mb: 2 }}>
            {Object.entries(AVAILABLE_PERMISSIONS).map(([key, label]) => {
              const currentPermissions =
                changes?.permissions ||
                selectedUser?.permissions ||
                DEFAULT_PERMISSIONS;
              const isChecked = currentPermissions[key] || false;

              return (
                <FormControlLabel
                  key={key}
                  control={
                    <Switch
                      checked={isChecked}
                      onChange={handlePermissionChange(key)}
                      disabled={isLoading}
                      color="primary"
                    />
                  }
                  label={label}
                  sx={{ display: "block", mb: 1 }}
                />
              );
            })}
          </Box>
        </>
      )}
      {(selectedUser?.role === "client" || selectedUser?.role === "employee") && (
        <>
          <Divider sx={{ mt: 3, mb: 2 }} textAlign="left">
            Projects related
          </Divider>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth size="small">
                <Autocomplete
                  multiple
                  options={projectsList}
                  getOptionLabel={(option) => option?.label ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.value === value?.value
                  }
                  value={selectedProjects}
                  onChange={(_event, newValue) => {
                    // Keep the handler safe when an option cannot be
                    // resolved: throwing here left `changes` empty, which is
                    // what the "No changes to save" message was reporting.
                    const visible = (newValue as any[])
                      .filter(Boolean)
                      .map((item) => item.value);

                    // An assignment from the other book has no option in
                    // this list. Keep it on the user rather than dropping it
                    // just because this screen cannot label it.
                    const known = new Set(
                      projectsList.map((project) => project.value),
                    );
                    const hidden = (selectedUser?.projects || [])
                      .map((id: any) => String(id))
                      .filter((id: string) => !known.has(id));

                    dispatch(handleProjectChange([...hidden, ...visible]));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} size="small" />
                  )}
                  renderTags={(selected, getTagProps) =>
                    selected.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        label={option?.label ?? ""}
                        size="small"
                      />
                    ))
                  }
                />
              </FormControl>
            </Grid>
          </Grid>
        </>
      )}
      <Divider sx={{ mt: 3, mb: 2 }} textAlign="right">
        Actions
      </Divider>
      <Grid container spacing={2} sx={{ justifyContent: "flex-end" }}>
        <Grid size={{ xs: 6, md: 3, lg: 3 }}>
          <Button
            fullWidth
            onClick={onClose}
            size="small"
            variant="outlined"
            color="primary"
            disabled={isLoading}
          >
            Cancel
          </Button>
        </Grid>
        <Grid size={{ xs: 6, md: 3, lg: 3 }}>
          <Button
            fullWidth
            onClick={handleSubmit}
            size="small"
            variant="contained"
            color="primary"
            disabled={isLoading}
          >
            Save
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default Info;
